import React from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useImageUploader from './useImageUploader';
import DragAndDropComponent from '../DragAndDropComponent';
import CropImage from './CropImage';
import useNotif from 'src/hooks/useNotif';
import { Alert } from '@material-ui/lab';

const ALLOWED_FILES = ['image/png', 'image/jpg', 'image/jpeg'];

const useStyles = makeStyles(theme => ({
  root: {
    '& > *': {
      margin: theme.spacing(1)
    }
  },
  boxInput: {
    '& > *': {
      margin: theme.spacing(1)
    },

    width: '100%',
    height: '233px',
    minHeight: '233px'
  },
  input: {
    display: 'none'
  },
  button: {
    border: '5px dashed #e6e6e6',
    height: '100%',
    width: '100%'
  },
  dialog: {
    height: '500px',
    position: 'relative',
    padding: '.5em',
    overflow: 'hidden',

    [theme.breakpoints.down('md')]: {
      height: '100%'
    },
    [theme.breakpoints.down('xs')]: {
      height: '100%'
    }
  }
}));

function FileUploadButton({ onChange }) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <input
        className={classes.input}
        onChange={onChange}
        id="icon-button-file"
        type="file"
        accept="image/*"
      />
      <label htmlFor="icon-button-file">
        <Button variant="contained" size="small" component="span">
          Browse
        </Button>
      </label>
    </div>
  );
}

function ImageUploader({
  open = false,
  handleClose = () => null,
  url = '',
  fileName = '',
  aspect = 0,
  overrideFilename = false,
  cropImgProps = {},
  callBack,
  defer = false
}) {
  const classes = useStyles();
  const {
    upload,
    crop,
    zoom,
    loading,
    upImg,
    imgName,
    onZoom,
    onCrop,
    onClear,
    setImgName,
    onChangeFile,
    onSetCropArea,
    onCropComplete,
    buildFile
  } = useImageUploader({
    url,
    onClose: handleClose,
    callBack
  });
  const notify = useNotif();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down('sm'));
  const md = useMediaQuery(theme.breakpoints.down('md'));
  const xs = useMediaQuery(theme.breakpoints.down('xs'));
  const mediaQ = md || sm || xs;

  function generateFileName(fileName) {
    if (fileName === 'image.png') return `IMG${new Date().getTime()}.png`;

    return fileName.toString().replaceAll('_', ' ');
  }

  function handleChangeFile(e) {
    const file = e.target.files[0];

    if (!ALLOWED_FILES.includes(file.type)) {
      notify.error(`Invalid file type`);
      return;
    }

    onChangeFile(file);
    if (overrideFilename) setImgName(fileName);
    else setImgName(generateFileName(file.name));
  }

  function handleOnDrop(files) {
    const file = files[0];

    if (!ALLOWED_FILES.includes(file.type)) {
      notify.error(`Invalid file type`);
      return;
    }

    onChangeFile(file);
    if (overrideFilename) setImgName(fileName);
    else setImgName(generateFileName(file.name));
  }

  function handleOnPaste(files) {
    const file = files[0];

    if (!ALLOWED_FILES.includes(file.type)) {
      notify.error(`Invalid file type`);
      return;
    }

    if (overrideFilename) setImgName(fileName);
    else setImgName(generateFileName(file.name));

    onChangeFile(file);
  }

  return (
    <Dialog
      fullWidth
      fullScreen={mediaQ}
      maxWidth="md"
      open={open}
      onClose={handleClose}
      aria-labelledby="max-width-dialog-title"
      disableEscapeKeyDown
    >
      <DialogTitle id="max-width-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2"> Upload Image </Typography>
          {upImg && (
            <Button color="primary" size="small" onClick={onClear}>
              Clear
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent classes={{ root: classes.dialog }}>
        <DragAndDropComponent onDropItem={handleOnDrop} onPaste={handleOnPaste}>
          {!upImg ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Typography
                variant="body1"
                color="textSecondary"
                component={Box}
                ml={2}
              >
                Drop, Paste or Browse here
              </Typography>
              <FileUploadButton onChange={handleChangeFile} />
            </Box>
          ) : (
            <CropImage
              aspect={aspect}
              crop={crop}
              zoom={zoom}
              image={upImg}
              onZoom={onZoom}
              onCrop={onCrop}
              onSetCropArea={onSetCropArea}
              onCropComplete={onCropComplete}
              {...cropImgProps}
            />
          )}
        </DragAndDropComponent>
      </DialogContent>
      <Box p={1}>
        <Alert severity="info">Scroll to resize</Alert>
      </Box>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={loading}>
          Cancel
        </Button>

        {defer ? (
          <Button
            onClick={async () => {
              const file = await buildFile();
              if (callBack && file) callBack(imgName, file);
              handleClose();
            }}
            color="primary"
            variant="contained"
            disabled={loading || !upImg}
          >
            Attach
          </Button>
        ) : (
          <Button
            onClick={upload}
            color="primary"
            variant="contained"
            disabled={loading || !upImg}
          >
            Upload
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ImageUploader;
